import type { StatsParams } from '@empathybroker/tsclient-stats/dist/models/params/stats-params'
import type { QueriesResponse } from '@empathybroker/tsclient-stats/dist/models/queries/queries.response'
import type { ActionContext } from 'vuex'
import type { ActionClass, AnalyticsState, DateFilter, Filter, Pagination } from '../../../models'
import type { ContextProvider } from '../../../providers'
import { FormatEnum } from '@empathybroker/tsclient-stats/dist/enums/format.enum'
import { QueryTypeEnum as StatsQueryTypeEnum } from '@empathybroker/tsclient-stats/dist/enums/query-type.enum'
import { StatsReportsIdsEnum } from '@empathybroker/tsclient-stats/dist/enums/stats-reports-ids.enum'
import { CONTAINER } from '../../../config'
import { TYPES } from '../../../config/inversify.types'
import { FilterTypeEnum, QueriesTypeEnum } from '../../../enums'
import { DownloadUtils } from '../../../utils/download.utils'
import { ANALYTICS_CONSTS } from '../analytics.consts'

export class LoadQueriesDataAction implements ActionClass {
  static async execute(
    this: void,
    { dispatch, commit, state }: ActionContext<AnalyticsState, any>,
    payload: {
      type: QueriesTypeEnum
      pagination: Pagination
      download?: boolean
      downloadFields?: string[]
      filters?: StatsParams['filters']
    },
  ): Promise<QueriesResponse | undefined> {
    let queriesData: QueriesResponse | undefined = state.data.queries[payload.type]
    const offset = (payload.pagination.page - 1) * payload.pagination.itemsPerPage
    if (
      payload.download ||
      !queriesData ||
      queriesData.params.rows !== payload.pagination.itemsPerPage ||
      queriesData.params.offset !== offset
    ) {
      try {
        queriesData = await dispatch(ANALYTICS_CONSTS.actions.fetchStatsReport, {
          id: StatsReportsIdsEnum.QUERIES,
          params: {
            type: LoadQueriesDataAction.getStatsType(payload.type),
            rows: payload.pagination.itemsPerPage,
            offset,
            format: payload.download ? FormatEnum.CSV : FormatEnum.JSON,
            ...(payload.download &&
              !!payload.downloadFields?.length && { csvFields: payload.downloadFields }),
            ...(payload.filters &&
              Object.keys(payload.filters).length && { filters: payload.filters }),
          },
        })
        if (!payload.download) {
          commit(ANALYTICS_CONSTS.mutations.setQueriesData, {
            data: queriesData,
            queriesType: payload.type,
          })
        } else if (queriesData) {
          const fileName = LoadQueriesDataAction.getDownloadFileName(
            payload.type,
            payload.pagination,
          )
          DownloadUtils.downloadCSV(queriesData.data.toString(), fileName)
        }
      } catch (error) {
        throw error
      }
    }
    return queriesData
  }

  private static getStatsType(type: QueriesTypeEnum): StatsQueryTypeEnum {
    return {
      [QueriesTypeEnum.ALL]: StatsQueryTypeEnum.ALL,
      [QueriesTypeEnum.WITH_RESULTS]: StatsQueryTypeEnum.WITH_RESULTS,
      [QueriesTypeEnum.WITHOUT_RESULTS]: StatsQueryTypeEnum.WITHOUT_RESULTS,
    }[type]
  }

  private static getDownloadFileName(type: QueriesTypeEnum, pagination: Pagination): string {
    const { filters } = CONTAINER.get<ContextProvider>(TYPES.ContextProvider)()
    const typeString = LoadQueriesDataAction.getStatsType(type)
    const dateFilter = Object.values(filters).find(
      (filter: Filter) => filter.config.type === FilterTypeEnum.DATE,
    ) as DateFilter
    const startDate = new Date(dateFilter.selectedValues[0])
    const endDate = new Date(dateFilter.selectedValues[1])
    endDate.setUTCDate(endDate.getUTCDate() - 1)
    const startDateString = `${startDate.getUTCFullYear()}-${
      startDate.getUTCMonth() + 1
    }-${startDate.getUTCDate()}`
    const endDateString = `${endDate.getUTCFullYear()}-${
      endDate.getUTCMonth() + 1
    }-${endDate.getUTCDate()}`
    return `queries-${typeString}_${pagination.itemsPerPage}_${startDateString}-${endDateString}`
  }
}
